import LinkButton from '@carvana/ds-link-button';
import { SearchSource } from '@carvana/merch-contracts';
import cx from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import AnimateHeight, {
  OPEN_ANIMATION_DURATION_SECONDS
} from '@/common/AnimateHeight';
import FilterEvents from '~/constants/srp/events';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useIsMobile } from '~/context/common/DeviceContext';
import FacetDataContext from '~/context/common/FacetDataContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import useGetFilterTags from '~/hooks/common/searches/useGetFilterTags';
import Tag from './Tag';

type OwnProps = {
  inModal?: boolean;
};

const MAX_TAGS = 5;

export default function AppliedFilters({ inModal = false }: OwnProps) {
  const appliedFiltersRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { baseline } = useContext(FacetDataContext);
  const { appliedFilters, clearAllFilters } = useContext(AppliedFiltersContext);
  const isDealership = useIsDealershipPage();
  const filterTags = useGetFilterTags(baseline, appliedFilters, {
    displayNewCars: !isDealership,
    includeVehicleAlerts: true
  });
  const [clearAll, setClearAll] = useState(false);
  const [isExpanded, setExpanded] = useState(filterTags.length <= MAX_TAGS);
  const canExpand = filterTags.length > MAX_TAGS;
  const trackEvent = useTrackEvent();

  const handleClearAllTags = () => {
    setSearchSource(SearchSource.TagRemoved);
    setClearAll(true);
    clearAllFilters();
    trackEvent(FilterEvents.removedFilter, {
      item: 'clear all button',
      label: 'Clear All'
    });
  };

  const showAppliedTags = filterTags.length > 0 && !clearAll;

  useEffect(() => {
    setClearAll(false);
  }, [filterTags, appliedFilters]);

  useEffect(() => {
    if (filterTags.length <= MAX_TAGS && isExpanded) {
      setExpanded(false);
    }
  }, [filterTags, isExpanded]);

  // Set CSS variable for Applied Filters height (mobile only) for FilterDrawer scrolling
  useEffect(() => {
    if (isMobile && inModal) {
      if (appliedFiltersRef.current && showAppliedTags) {
        const timeoutId = setTimeout(() => {
          if (appliedFiltersRef.current) {
            const height = appliedFiltersRef.current.offsetHeight;
            document.documentElement.style.setProperty(
              '--applied-filters-height',
              `${height}px`
            );
          }
        }, OPEN_ANIMATION_DURATION_SECONDS * 1000);

        return () => clearTimeout(timeoutId);
      } else {
        document.documentElement.style.setProperty(
          '--applied-filters-height',
          '0px'
        );
      }
    }
  }, [isMobile, inModal, showAppliedTags, isExpanded, filterTags.length]);

  return (
    <AnimateHeight
      show={showAppliedTags}
      className={cx({
        'border-b border-border-subtle': showAppliedTags
      })}
    >
      <div
        className="flex flex-col gap-xl bg-background-weak pt-2xl"
        data-testid="applied-filters"
        ref={appliedFiltersRef}
      >
        <div className="flex w-full justify-between px-2xl t-strong-sm">
          <span className="text-text-strong">
            Applied Filters <span>({filterTags.length.toLocaleString()})</span>
          </span>
          <LinkButton
            size="sm"
            data-adobe-id="clear-all-applied-filters"
            data-analytics-id={null}
            data-testid="clear-all-applied-filters"
            onClick={handleClearAllTags}
            underline={false}
          >
            Clear All
          </LinkButton>
        </div>

        {/* show max 5 lines on mobile. shows max 7 lines on tablet and desktop */}
        <div className="flex max-h-[220px] w-full flex-wrap gap-md overflow-y-scroll px-2xl pb-2xl sm:max-h-[305px]">
          {filterTags.slice(0, isExpanded ? undefined : MAX_TAGS).map(tag => (
            <Tag key={`${tag.facetKey}-${tag.tagName}`} filterTag={tag} />
          ))}

          {canExpand && (
            <LinkButton
              size="sm"
              underline={false}
              data-testid="applied-filters-show-more"
              onClick={() => {
                setExpanded(v => !v);
              }}
              data-adobe-id="view-all-applied-filters"
              data-analytics-id="view-all-applied-filters"
            >
              Show {isExpanded ? 'Fewer' : `${filterTags.length - 5} More`}
            </LinkButton>
          )}
        </div>
      </div>
    </AnimateHeight>
  );
}
